import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useApolloClient } from '@apollo/react-hooks';
import useUser from 'hooks/use-user';
import { recordUserAcceptance as recordUserAcceptanceMutation } from 'src/payments/graphql/mutations/record-user-acceptance.gql';
import { EntityTypes } from 'types/graphql';
import { useStores } from 'contexts/stores';
import { ShowEnrollment } from 'src/payments/graphql/queries/show-enrollment.gql';
import { ERNIE_DEFAULT_MESSAGES, ERNIE_TIMEOUTS, ERNIE_TYPES } from 'shared/constants';
import useErnie from 'shared/hooks/use-ernie';

type RecordUserAcceptanceResult = {
  recordUserAcceptance: () => Promise<boolean>;
};

export const useRecordUserAcceptance = (): RecordUserAcceptanceResult => {
  const apolloClient = useApolloClient();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const showErnie = useErnie();
  const { Cart } = useStores();
  const User = useUser();

  const recordUserAcceptance = async (): Promise<boolean> => {
    DutchiePayEnrollment.userAcceptanceRequested();
    const recordUserAcceptanceProps = {
      // TODO: once we have the ToC api up, that'll provide us with
      // an acceptanceDocumentId to pass along here
      acceptanceDocumentId: `v1`,
      entityId: User.id,
      entityType: EntityTypes.user,
      userId: User.id,
    };

    try {
      const response = await apolloClient.mutate({
        mutation: recordUserAcceptanceMutation,
        variables: recordUserAcceptanceProps,
        refetchQueries: [{ query: ShowEnrollment }],
        awaitRefetchQueries: true,
      });

      const success = response.data?.recordUserAcceptance?.success;

      if (response.errors || !success) {
        DutchiePayEnrollment.termsOfServiceError();
        showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
        return false;
      }

      if (success) {
        if (DutchiePayEnrollment.autoCheckoutAfterBankLinkReconnect) {
          Cart.setAutoCheckoutAfterBankLinkReconnect(true);
        }
        DutchiePayEnrollment.userAcceptanceSucceeded();
      }

      return success;
    } catch (err) {
      DutchiePayEnrollment.termsOfServiceError();
      showErnie(ERNIE_DEFAULT_MESSAGES.ERROR, ERNIE_TYPES.DANGER, ERNIE_TIMEOUTS.LONG);
      console.error(err);
      return false;
    }
  };

  return {
    recordUserAcceptance,
  };
};
